import PageStructure, { PageContent, PageContentInner } from "@pages/PageStructure.component";
import { getGroupValueForNodeType } from "@services/modeling.service";
import { savePipelineNode, useMissionControlDataFlowData } from "@stores/data.store";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ReportBuilderDimensionORM, ReportBuilderMeasureORM, useDimensions } from "@models/reportBuilder";
import PipelineNodeList from "@components/pipelineNodes/PipelineNodeList.component";
import { DashboardIcon } from "@components/pipelineNodes/PipelineNodeIcon.component";
import { getPromptAnswer } from "@services/alert/alert.service";
import toast from "@services/toast.service";
import { getErrorMessage } from "@services/errors.service";
import ObjectListHeader from "@components/common/ObjectListHeader.component";
import MetricsList from "@components/metrics/MetricsList.component";
import DimensionsList from "@components/dimensions/DimensionsList.component";
import SemanticLayerHeader from "@components/common/SemanticLayerHeader.component";

const DataModelTablePage = () => {
    const [search, setSearch] = useState('');
    const { data: dimensions } = useDimensions();
    const data = useMissionControlDataFlowData();
    const location = useLocation();
    const navigate = useNavigate();

    const entities = useMemo(() => {
        if (!data.data) return [];
        return data.data.nodes.filter(node => 
            getGroupValueForNodeType(node.data.nodeType) === 'DATA_MODELING'
        );
    }, [data.data]);

    // Determine active view from location
    const activeView = useMemo(() => {
        const path = location.pathname;
        if (path.includes('/dimensions')) {
            return 'dimensions';
        } else if (path.includes('/metrics')) {
            return 'metrics';
        } else {
            return 'entities';
        }
    }, [location.pathname]);

    const createNewEntity = useCallback(async () => {
        const newName = await getPromptAnswer('What do you want to name this entity?', 'New Entity');
        if (!newName) {
            return;
        }

        try {
            const newEntity = await savePipelineNode({
                id: null,
                node_type: 'DIMENSION',
                name: newName,
                label: newName,
                description: '',
                upstream_node_ids: [],
                fields: [],
                table_name: '',
            });
            navigate('/node/' + newEntity.id + '/config');
        } catch (err) {
            toast('danger', 'Error', getErrorMessage(err));
        }
    }, []);

   
    const createNewMetric = useCallback(async () => {
        const newName = await getPromptAnswer('What do you want to name this metric?', 'New Metric');
        if (!newName) {
            return;
        }

        try {
            const newMetric = await ReportBuilderMeasureORM.save({
                id: null,
                name: newName,
                column_name: newName,
                description: '',
            });
            navigate('/metrics/' + newMetric.id);
        } catch (err) {
            toast('danger', 'Error', getErrorMessage(err));
        }
    }, []);

    const createNewDimension = useCallback(async () => {
        const newName = await getPromptAnswer('What do you want to name this dimension?', 'New Dimension');
        if (!newName) {
            return;
        }
        
        try {
            const newDimension = await ReportBuilderDimensionORM.save({
                id: null,
                name: newName,
                column_name: newName,
                description: '',
                pipeline_node_id: '',
                field_id: '',
            });
            navigate('/model/dimension/' + newDimension.id);
        } catch (err) {
            toast('danger', 'Error', getErrorMessage(err));
        }
    }, []);

    return (
        <PageStructure>
            <PageContent>
                <SemanticLayerHeader
                    activePage={activeView}
                    search={search}
                    onSearchChange={setSearch}
                />
                <PageContentInner hasHeader>
                    <div className="p-4">
                        {activeView === 'entities' && (
                            <PipelineNodeList
                                headerIcon="mdi mdi-table-star"
                                headerIconColor="purple"
                                title="Entities"
                                actionButtonText="New Entity"
                                actionButtonIcon="mdi mdi-plus-thick"
                                actionButtonOnClick={createNewEntity}
                                searchQuery={search}
                                nodeFilter={(node) => getGroupValueForNodeType(node.node_type) === 'DATA_MODELING'}
                            />
                        )}
                        {activeView === 'metrics' && (
                            <>
                                <ObjectListHeader
                                    headerIcon={<DashboardIcon compact bgColor='green' icon="mdi mdi-pound-box"/>}
                                    title="Metrics"
                                    actionButtonText="New Metric"
                                    actionButtonIcon="mdi mdi-plus-thick"
                                    actionButtonOnClick={createNewMetric}
                                />  
                                <MetricsList searchQuery={search}/>
                            </>
                        )}
                        {activeView === 'dimensions' && (
                            <>
                                <ObjectListHeader
                                    headerIcon={<DashboardIcon compact bgColor='blue' icon="mdi mdi-axis-arrow"/>}
                                    title="Dimensions"
                                    actionButtonText="New Dimension"
                                    actionButtonIcon="mdi mdi-plus-thick"
                                    actionButtonOnClick={createNewDimension}
                                />  
                                <DimensionsList searchQuery={search}/>
                            </>
                        )}
                    </div>
                </PageContentInner>
            </PageContent>
        </PageStructure>
    );
};

export default DataModelTablePage;